 //pdf merger + stream write to disk utility.
 //merges multiple mini-reel PDFs (stored in IndexedDB) into a single PDF and
 //streams the result to disk via the File System Access API. Falls back to a
 //traditional blob download when streaming is unavailable.
 

import { PDFDocument } from 'pdf-lib';
import { fileSave } from 'browser-fs-access';
import { getPdf } from './pdfStorage';

export interface MergeAndStreamOptions {
  reelKeys: string[];
  filename: string;
  onMergeProgress?: (merged: number, total: number) => void;
  onSerializing?: () => void;
  onWriting?: () => void;
}

 
//this is supported in Chromium-based browsers (Chrome, Edge, Opera).
export function supportsStreamSave(): boolean {
  return typeof window !== 'undefined' && 'showSaveFilePicker' in window;
}

async function acquireFileHandle(filename: string): Promise<FileSystemFileHandle | null> {
  if (!supportsStreamSave()) return null;

  return await (window as any).showSaveFilePicker({
    suggestedName: filename,
    types: [{
      description: 'PDF Document',
      accept: { 'application/pdf': ['.pdf'] },
    }],
  });
}

export async function mergeAndStreamPdfs(options: MergeAndStreamOptions): Promise<void> {
  const { reelKeys, filename, onMergeProgress, onSerializing, onWriting } = options;

  if (reelKeys.length === 0) {
    throw new Error('No reel keys provided for merge');
  }

  //acquire the file handle immediately as it must happen within the user gesture.
  const fileHandle = await acquireFileHandle(filename);

  //single reel, no merge needed
  if (reelKeys.length === 1) {
    const blob = await getPdf(reelKeys[0]);
    if (!blob) {
      throw new Error(`Mini-reel PDF not found in IndexedDB: ${reelKeys[0]}`);
    }
    onMergeProgress?.(1, 1);
    onWriting?.();

    if (fileHandle) {
      const writable = await fileHandle.createWritable();
      try {
        await writable.write(blob);
        await writable.close();
      } catch (err) {
        try { await writable.abort(); } catch { /* ignore */ }
        throw err;
      }
    } else {
      await fileSave(blob, {
        fileName: filename,
        extensions: ['.pdf'],
        description: 'PDF Document',
        mimeTypes: ['application/pdf'],
      });
    }
    return;
  }

  //multi-reel merge path
  const mergeDoc = await PDFDocument.create();

  for (let i = 0; i < reelKeys.length; i++) {
    const key = reelKeys[i];
    const blob = await getPdf(key);
    if (!blob) {
      throw new Error(`Mini-reel PDF not found in IndexedDB: ${key}`);
    }

    const arrayBuffer = await blob.arrayBuffer();

    const reelDoc = await PDFDocument.load(arrayBuffer, {
      ignoreEncryption: true,
    });

    const pageIndices = reelDoc.getPageIndices();
    const copiedPages = await mergeDoc.copyPages(reelDoc, pageIndices);
    for (const page of copiedPages) {
      mergeDoc.addPage(page);
    }

    onMergeProgress?.(i + 1, reelKeys.length);
  }

  //serialize the merged document
  onSerializing?.();
  const pdfBytes = await mergeDoc.save();

  //write to disk
  onWriting?.();

  if (fileHandle) {
    const writable = await fileHandle.createWritable();
    try {
      await writable.write(pdfBytes as unknown as FileSystemWriteChunkType);
      await writable.close();
    } catch (err) {
      try { await writable.abort(); } catch (e) {console.log(e);}
      throw err;
    }
  } else {
    //fallback: use browser-fs-access fileSave
    const blob = new Blob([pdfBytes as BlobPart], { type: 'application/pdf' });
    await fileSave(blob, {
      fileName: filename,
      extensions: ['.pdf'],
      description: 'PDF Document',
      mimeTypes: ['application/pdf'],
    });
  }
}
